import React, { useEffect, useMemo, useRef } from 'react';

const clamp01 = (v) => Math.max(0, Math.min(1, v));

/** Linear interpolation between the two Transform keyframes surrounding
 * `localTime`; falls back to the item's base `transform` with 0–1
 * keyframes. Keyframe times are timeline-relative (0 = item start). Used
 * for both video AND text items — both carry the same generic
 * transform+keyframes shape. */
const interpolateTransform = (item, localTime) => {
  const base = item.transform || { scale_percent: 100, position_x: 0, position_y: 0, rotation_degrees: 0, opacity_percent: 100 };
  const kfs = item.keyframes || [];
  if (kfs.length === 0) return base;
  const sorted = [...kfs].sort((a, b) => a.time_seconds - b.time_seconds);
  if (sorted.length === 1 || localTime <= sorted[0].time_seconds) return sorted[0];
  if (localTime >= sorted[sorted.length - 1].time_seconds) return sorted[sorted.length - 1];
  let a = sorted[0];
  let b = sorted[sorted.length - 1];
  for (let i = 0; i < sorted.length - 1; i++) {
    if (localTime >= sorted[i].time_seconds && localTime <= sorted[i + 1].time_seconds) { a = sorted[i]; b = sorted[i + 1]; break; }
  }
  const span = b.time_seconds - a.time_seconds || 1;
  const t = (localTime - a.time_seconds) / span;
  const lerp = (x, y) => x + (y - x) * t;
  return {
    scale_percent: lerp(a.scale_percent, b.scale_percent),
    position_x: lerp(a.position_x, b.position_x),
    position_y: lerp(a.position_y, b.position_y),
    rotation_degrees: lerp(a.rotation_degrees, b.rotation_degrees),
    opacity_percent: lerp(a.opacity_percent, b.opacity_percent),
  };
};

const fadeMultiplier = (localTime, duration, fadeIn, fadeOut) => {
  let m = 1;
  if (fadeIn > 0 && localTime < fadeIn) m = Math.min(m, localTime / fadeIn);
  if (fadeOut > 0 && localTime > duration - fadeOut) m = Math.min(m, Math.max(0, (duration - localTime) / fadeOut));
  return clamp01(m);
};

/** Generic linear interpolation over a keyframe list keyed by
 * `time_seconds`, for any shape of numeric fields (effect intensity,
 * blur-region bounds). `fields` lists which numeric keys to interpolate. */
function interpolateKeyframeList(base, keyframes, localTime, fields) {
  const kfs = keyframes || [];
  if (kfs.length === 0) return base;
  const sorted = [...kfs].sort((a, b) => a.time_seconds - b.time_seconds);
  if (sorted.length === 1 || localTime <= sorted[0].time_seconds) return sorted[0];
  if (localTime >= sorted[sorted.length - 1].time_seconds) return sorted[sorted.length - 1];
  let a = sorted[0];
  let b = sorted[sorted.length - 1];
  for (let i = 0; i < sorted.length - 1; i++) {
    if (localTime >= sorted[i].time_seconds && localTime <= sorted[i + 1].time_seconds) { a = sorted[i]; b = sorted[i + 1]; break; }
  }
  const span = b.time_seconds - a.time_seconds || 1;
  const t = (localTime - a.time_seconds) / span;
  const out = {};
  fields.forEach((f) => { out[f] = a[f] + (b[f] - a[f]) * t; });
  return out;
}

const hexToRgba = (hex, alphaPercent) => {
  if (!hex) return undefined;
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const bigint = parseInt(full, 16);
  if (Number.isNaN(bigint)) return undefined;
  const r = (bigint >> 16) & 255, g = (bigint >> 8) & 255, b = bigint & 255;
  return `rgba(${r},${g},${b},${clamp01(alphaPercent / 100)})`;
};

/** Deterministic, documented CSS-filter approximation of the stored
 * color-grade numbers (see MasterColorAdjustments in all_models.py).
 * Highlights/Shadows have no faithful CSS-only primitive and are
 * intentionally NOT applied here — the numeric values stay the
 * authoritative source for the Vultr render worker regardless. */
const buildColorFilterCss = (color) => {
  if (!color) return '';
  const parts = [];
  const brightnessMul = 1 + ((color.brightness || 0) + (color.exposure || 0) * 0.5) / 100;
  if (brightnessMul !== 1) parts.push(`brightness(${Math.max(0, brightnessMul).toFixed(3)})`);
  const contrastMul = 1 + (color.contrast || 0) / 100;
  if (contrastMul !== 1) parts.push(`contrast(${Math.max(0, contrastMul).toFixed(3)})`);
  const saturateMul = 1 + (color.saturation || 0) / 100;
  if (saturateMul !== 1) parts.push(`saturate(${Math.max(0, saturateMul).toFixed(3)})`);
  const hueDeg = (color.tint || 0) * 0.6 - (color.temperature || 0) * 0.6;
  if (hueDeg) parts.push(`hue-rotate(${hueDeg.toFixed(2)}deg)`);
  const blend = clamp01((color.filter_intensity ?? 100) / 100);
  if (color.filter_preset === 'mono') parts.push(`grayscale(${blend.toFixed(2)})`);
  else if (color.filter_preset === 'vivid') parts.push(`saturate(${(1 + 0.35 * blend).toFixed(3)}) contrast(${(1 + 0.12 * blend).toFixed(3)})`);
  else if (color.filter_preset === 'warm') parts.push(`sepia(${(0.35 * blend).toFixed(3)}) hue-rotate(${(-8 * blend).toFixed(2)}deg)`);
  else if (color.filter_preset === 'cool') parts.push(`hue-rotate(${(10 * blend).toFixed(2)}deg)`);
  else if (color.filter_preset === 'vintage') parts.push(`sepia(${(0.3 * blend).toFixed(3)}) contrast(${(1 - 0.08 * blend).toFixed(3)}) saturate(${(1 - 0.15 * blend).toFixed(3)})`);
  return parts.join(' ');
};

const interpolateEffectIntensity = (effect, localTime) =>
  interpolateKeyframeList({ intensity_percent: effect.intensity_percent ?? 100 }, effect.keyframes, localTime, ['intensity_percent']).intensity_percent;

/** blur/glow have faithful CSS equivalents and genuinely drive the live
 * preview; vignette is applied as a separate overlay (below); sharpen has
 * no faithful CSS-only technique and is deliberately NOT applied here —
 * it only ever shows as "render-only" in the Inspector. */
const buildEffectsFilterCss = (effects, localTime) => {
  const parts = [];
  [...(effects || [])].filter((e) => e.enabled).sort((a, b) => a.order - b.order).forEach((effect) => {
    const intensity = interpolateEffectIntensity(effect, localTime) / 100;
    if (effect.type === 'blur') parts.push(`blur(${(intensity * 8).toFixed(2)}px)`);
    else if (effect.type === 'glow') parts.push(`drop-shadow(0 0 ${(intensity * 10).toFixed(1)}px rgba(255,255,255,${(intensity * 0.65).toFixed(2)}))`);
  });
  return parts.join(' ');
};

const vignetteIntensity = (effects, localTime) => {
  const eff = (effects || []).find((e) => e.type === 'vignette' && e.enabled);
  if (!eff) return 0;
  return interpolateEffectIntensity(eff, localTime) / 100;
};

const interpolateBlurRegion = (region, localTime) =>
  interpolateKeyframeList(
    { x_percent: region.x_percent, y_percent: region.y_percent, width_percent: region.width_percent, height_percent: region.height_percent },
    region.keyframes, localTime, ['x_percent', 'y_percent', 'width_percent', 'height_percent'],
  );

/** Real CSS blend of the outgoing (previous) and incoming (active) item
 * during a transition window — never a decorative no-op. `progress` is
 * 0→1 across `transition.duration_seconds`. */
const computeTransitionStyle = (role, transition, progress) => {
  if (!role || !transition) return { opacityMul: 1, extraTransform: '', clipPath: '' };
  const p = clamp01(progress);
  switch (transition.type) {
    case 'crossfade':
      return { opacityMul: role === 'outgoing' ? 1 - p : p, extraTransform: '', clipPath: '' };
    case 'fade_through_black':
      return role === 'outgoing'
        ? { opacityMul: Math.max(0, 1 - p * 2), extraTransform: '', clipPath: '' }
        : { opacityMul: Math.max(0, p * 2 - 1), extraTransform: '', clipPath: '' };
    case 'wipe': {
      const dir = transition.direction || 'left';
      const pct = p * 100;
      const clip = dir === 'left' ? `inset(0 0 0 ${pct}%)` : dir === 'right' ? `inset(0 ${pct}% 0 0)` : dir === 'up' ? `inset(${pct}% 0 0 0)` : `inset(0 0 ${pct}% 0)`;
      return role === 'incoming' ? { opacityMul: 1, extraTransform: '', clipPath: clip } : { opacityMul: 1, extraTransform: '', clipPath: '' };
    }
    case 'slide': {
      const dir = transition.direction || 'left';
      const sign = (dir === 'left' || dir === 'up') ? 1 : -1;
      const axis = (dir === 'left' || dir === 'right') ? 'X' : 'Y';
      return role === 'incoming'
        ? { opacityMul: 1, extraTransform: `translate${axis}(${(sign * (1 - p) * 100).toFixed(1)}%)`, clipPath: '' }
        : { opacityMul: 1, extraTransform: `translate${axis}(${(-sign * p * 100).toFixed(1)}%)`, clipPath: '' };
    }
    case 'zoom':
      return role === 'incoming'
        ? { opacityMul: p, extraTransform: `scale(${(1.3 - 0.3 * p).toFixed(3)})`, clipPath: '' }
        : { opacityMul: 1 - p, extraTransform: '', clipPath: '' };
    default:
      return { opacityMul: 1, extraTransform: '', clipPath: '' };
  }
};

function VideoLayer({ item, source, playhead, isPlaying, fit, compWidth, compHeight, zIndex, onDurationKnown, transitionRole, transition, transitionProgress }) {
  const videoRef = useRef(null);
  const localTime = playhead - item.timeline_start_seconds;
  const speed = (item.speed_percent ?? 100) / 100;

  useEffect(() => {
    const el = videoRef.current;
    if (!el || !source?.stream_url) return;
    if (el.dataset.srcKey !== item.content_source_id) {
      el.src = source.stream_url;
      el.dataset.srcKey = item.content_source_id;
    }
    el.playbackRate = speed || 1;
    const desiredLocal = item.source_in_seconds + localTime * speed;
    if (Math.abs(el.currentTime - desiredLocal) > 0.35) el.currentTime = Math.max(0, desiredLocal);
    const audio = item.audio || {};
    const shouldSilence = !!(audio.muted || item.audio_detached);
    el.muted = shouldSilence;
    if (!shouldSilence) {
      const base = clamp01((audio.volume_percent ?? 100) / 100);
      el.volume = clamp01(base * fadeMultiplier(localTime, item.duration_seconds, audio.fade_in_seconds || 0, audio.fade_out_seconds || 0));
    }
    if (isPlaying && el.paused) el.play().catch(() => {});
    if (!isPlaying && !el.paused) el.pause();
  }, [item, source, playhead, isPlaying, localTime, speed]);

  const t = interpolateTransform(item, localTime);
  const crop = item.crop || { x: 0, y: 0, width: 100, height: 100 };
  const pctX = (t.position_x / (compWidth || 1920)) * 100;
  const pctY = (t.position_y / (compHeight || 1080)) * 100;

  const { opacityMul, extraTransform, clipPath } = computeTransitionStyle(transitionRole, transition, transitionProgress);
  const colorCss = buildColorFilterCss(item.color);
  const effectsCss = buildEffectsFilterCss(item.effects, localTime);
  const combinedFilter = [colorCss, effectsCss].filter(Boolean).join(' ') || undefined;
  const vignette = vignetteIntensity(item.effects, localTime);

  const outerStyle = {
    position: 'absolute', inset: 0, zIndex,
    transform: `translate(${pctX}%, ${pctY}%) rotate(${t.rotation_degrees}deg) scale(${(t.scale_percent ?? 100) / 100}) ${extraTransform}`,
    opacity: clamp01((t.opacity_percent ?? 100) / 100) * opacityMul,
    border: item.is_pip ? '2px solid rgba(58,123,255,0.6)' : undefined,
    clipPath: clipPath || undefined,
  };
  const innerStyle = {
    position: 'absolute',
    width: `${(100 / crop.width) * 100}%`,
    height: `${(100 / crop.height) * 100}%`,
    left: `${-(crop.x / crop.width) * 100}%`,
    top: `${-(crop.y / crop.height) * 100}%`,
    objectFit: fit === 'contain' ? 'contain' : 'cover',
    filter: combinedFilter,
  };

  return (
    <div style={outerStyle} data-testid={`preview-layer-${item.id}`}>
      <div style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
        <video
          ref={videoRef} style={innerStyle} playsInline
          onLoadedMetadata={(e) => onDurationKnown?.(e.currentTarget.duration)}
        />
        {vignette > 0 && (
          <div
            style={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: `radial-gradient(ellipse at center, transparent ${Math.max(10, 70 - vignette * 50)}%, rgba(0,0,0,${Math.min(0.9, vignette).toFixed(2)}) 100%)` }}
            data-testid={`preview-vignette-${item.id}`}
          />
        )}
        {(item.blur_regions || []).map((region) => {
          const r = interpolateBlurRegion(region, localTime);
          const px = (region.blur_intensity_percent ?? 80) / 100 * 20;
          return (
            <div
              key={region.id}
              style={{
                position: 'absolute',
                left: `${r.x_percent}%`, top: `${r.y_percent}%`,
                width: `${r.width_percent}%`, height: `${r.height_percent}%`,
                backdropFilter: `blur(${px.toFixed(1)}px)`,
                WebkitBackdropFilter: `blur(${px.toFixed(1)}px)`,
                pointerEvents: 'none',
              }}
              data-testid={`preview-blur-region-${region.id}`}
            />
          );
        })}
      </div>
    </div>
  );
}

function AudioOnlyLayer({ item, source, playhead, isPlaying }) {
  const audioRef = useRef(null);
  const localTime = playhead - item.timeline_start_seconds;
  const speed = (item.speed_percent ?? 100) / 100;

  useEffect(() => {
    const el = audioRef.current;
    if (!el || !source?.stream_url) return;
    if (el.dataset.srcKey !== item.content_source_id) {
      el.src = source.stream_url;
      el.dataset.srcKey = item.content_source_id;
    }
    el.playbackRate = speed || 1;
    const desiredLocal = item.source_in_seconds + localTime * speed;
    if (Math.abs(el.currentTime - desiredLocal) > 0.35) el.currentTime = Math.max(0, desiredLocal);
    const audio = item.audio || {};
    el.muted = !!audio.muted;
    if (!audio.muted) {
      const base = clamp01((audio.volume_percent ?? 100) / 100);
      el.volume = clamp01(base * fadeMultiplier(localTime, item.duration_seconds, audio.fade_in_seconds || 0, audio.fade_out_seconds || 0));
    }
    if (isPlaying && el.paused) el.play().catch(() => {});
    if (!isPlaying && !el.paused) el.pause();
  }, [item, source, playhead, isPlaying, localTime, speed]);

  return <audio ref={audioRef} data-testid={`preview-audio-${item.id}`} />;
}

/** Plain text/title overlay OR (when `text.segments` is populated) the
 * active caption line, with genuine entrance/exit animation and
 * per-word highlight driven by real stored timing — never a fabricated
 * shape. Transform/keyframes reuse the same generic interpolation as
 * video items. */
function TextLayer({ item, playhead, compWidth, compHeight }) {
  const localTime = playhead - item.timeline_start_seconds;
  const t = interpolateTransform(item, localTime);
  const text = item.text || {};
  const segments = text.segments || [];
  let displayText = text.text || '';
  let activeWords = null;
  let activeWordIndex = -1;
  if (segments.length > 0) {
    const seg = segments.find((s) => localTime >= s.start_seconds && localTime < s.end_seconds);
    if (!seg) return null;
    displayText = seg.text;
    if (text.animated_caption_style !== 'none' && seg.words?.length) {
      activeWords = seg.words;
      // Words use the SAME item-relative clock as the segment itself
      // (never segment-relative) — compare directly against localTime.
      activeWordIndex = seg.words.findIndex((w) => localTime >= w.start_seconds && localTime < w.end_seconds);
    }
  }
  if (!displayText) return null;

  const animation = text.animation || { entrance: 'none', exit: 'none', entrance_duration_seconds: 0.4, exit_duration_seconds: 0.4 };
  let animOpacity = 1;
  let animTransform = '';
  const applyAnim = (kind, p, outward) => {
    if (kind === 'fade') animOpacity = Math.min(animOpacity, p);
    else if (kind === 'pop') { animOpacity = Math.min(animOpacity, p); animTransform += ` scale(${(0.7 + 0.3 * p).toFixed(3)})`; }
    else if (kind === 'slide_up') { animOpacity = Math.min(animOpacity, p); animTransform += ` translateY(${((1 - p) * 40 * outward).toFixed(1)}px)`; }
    else if (kind === 'slide_left') { animOpacity = Math.min(animOpacity, p); animTransform += ` translateX(${((1 - p) * 60 * outward).toFixed(1)}px)`; }
  };
  if (animation.entrance !== 'none' && localTime < animation.entrance_duration_seconds) {
    applyAnim(animation.entrance, clamp01(localTime / Math.max(0.01, animation.entrance_duration_seconds)), 1);
  }
  const timeFromEnd = item.duration_seconds - localTime;
  if (animation.exit !== 'none' && timeFromEnd < animation.exit_duration_seconds) {
    applyAnim(animation.exit, clamp01(timeFromEnd / Math.max(0.01, animation.exit_duration_seconds)), -1);
  }

  const pctX = (t.position_x / (compWidth || 1920)) * 100;
  const pctY = (t.position_y / (compHeight || 1080)) * 100;
  const align = text.alignment === 'left' ? 'flex-start' : text.alignment === 'right' ? 'flex-end' : 'center';

  const outerStyle = {
    position: 'absolute', inset: 0, zIndex: 500,
    display: 'flex', alignItems: 'center', justifyContent: align,
    transform: `translate(${pctX}%, ${pctY}%) rotate(${t.rotation_degrees}deg) scale(${(t.scale_percent ?? 100) / 100})${animTransform}`,
    opacity: clamp01((t.opacity_percent ?? 100) / 100) * clamp01(animOpacity),
    pointerEvents: 'none', padding: '4%',
  };
  const hasBg = (text.background_opacity || 0) > 0;
  const spanStyle = {
    position: 'relative', display: 'inline-block', maxWidth: '92%',
    fontFamily: text.font_family || 'Inter', fontSize: `${text.font_size || 32}px`,
    fontWeight: text.font_weight === 'bold' ? 700 : 400, color: text.color || '#FFFFFF',
    textAlign: text.alignment || 'center',
    textShadow: text.shadow_enabled ? `0 0 ${text.shadow_blur ?? 4}px ${text.shadow_color || '#000'}` : undefined,
    WebkitTextStroke: text.outline_width ? `${(text.outline_width / 10).toFixed(2)}px ${text.outline_color || '#000'}` : undefined,
    padding: hasBg ? '4px 12px' : undefined,
    borderRadius: hasBg ? 4 : undefined,
    backgroundColor: hasBg ? hexToRgba(text.background_color || '#000000', text.background_opacity || 0) : undefined,
  };

  return (
    <div style={outerStyle} data-testid={`preview-text-${item.id}`}>
      <span style={spanStyle}>
        {activeWords ? activeWords.map((w, i) => (
          <span key={i} style={{ opacity: i === activeWordIndex ? 1 : 0.55, fontWeight: i === activeWordIndex ? 700 : spanStyle.fontWeight, marginRight: 4 }}>{w.text}</span>
        )) : displayText}
      </span>
    </div>
  );
}

function SafeZoneGuides() {
  return (
    <div className="absolute inset-0 pointer-events-none" data-testid="preview-safe-zone-guides">
      <div style={{ position: 'absolute', inset: '5%', border: '1px dashed rgba(255,255,255,0.3)' }} />
      <div style={{ position: 'absolute', inset: '10%', border: '1px dashed rgba(255,255,255,0.55)' }} />
    </div>
  );
}

const WATERMARK_POSITION_STYLE = {
  top_left: { top: '6%', left: '6%' },
  top_right: { top: '6%', right: '6%' },
  bottom_left: { bottom: '6%', left: '6%' },
  bottom_right: { bottom: '6%', right: '6%' },
};

/** Logo/watermark overlay sourced directly from the resolved Brand Kit's
 * own logo URL (set in branding.watermark.logo_url by UnifiedEditor.jsx
 * when the user enables it) — never a fabricated asset. Inset 6% from the
 * edge so it stays inside the Stage 3C safe-zone guide bands. */
function BrandWatermarkLayer({ watermark }) {
  if (!watermark?.enabled || !watermark.logo_url) return null;
  return (
    <img
      src={watermark.logo_url}
      alt="Brand watermark"
      style={{
        position: 'absolute', ...WATERMARK_POSITION_STYLE[watermark.position] || WATERMARK_POSITION_STYLE.bottom_right,
        width: `${watermark.width_percent ?? 12}%`, opacity: (watermark.opacity_percent ?? 85) / 100,
        zIndex: 900, pointerEvents: 'none', objectFit: 'contain',
      }}
      data-testid="preview-brand-watermark"
    />
  );
}

/**
 * Area 2 (extended) — composition-aware preview. Multiple visible video
 * tracks can overlap (PiP included); track order drives z-index; hidden
 * tracks never render; muted tracks/items/detached-video-audio never
 * contribute sound; transform/crop/opacity/speed/keyframes/fades/
 * color-grade/effects/privacy-blur all genuinely affect what is shown and
 * heard. Text overlays and captions render at their real timeline
 * positions, independent of video tracks. Transitions genuinely blend the
 * outgoing/incoming clip via CSS over the stored duration — never a
 * decorative no-op. Uses only native HTML5 <video>/<audio> + CSS — the
 * existing B2 presigned stream_url per source is the only media source,
 * never a new storage path. Stage 3B behavior is fully preserved: every
 * new field defaults to empty/None, so pre-Stage-3C compositions render
 * byte-for-byte as before.
 */
export function EditorCompositionPreview({ composition, siblingSources, playhead, isPlaying, fit, onDurationKnown }) {
  const videoLayers = useMemo(() => {
    if (!composition) return [];
    const videoTracks = composition.tracks.filter((t) => t.type === 'video' && !t.hidden).sort((a, b) => a.order - b.order);
    const layers = [];
    videoTracks.forEach((track, idx) => {
      const trackItems = composition.timeline_items
        .filter((i) => i.track_id === track.id && i.item_type === 'video')
        .sort((a, b) => a.timeline_start_seconds - b.timeline_start_seconds);
      const activeIdx = trackItems.findIndex((i) => playhead >= i.timeline_start_seconds && playhead < i.timeline_start_seconds + i.duration_seconds);
      if (activeIdx === -1) return;
      const active = trackItems[activeIdx];
      const transition = active.transition_in;
      const localStart = playhead - active.timeline_start_seconds;
      const transitionActive = !!(transition && transition.type !== 'cut' && transition.duration_seconds > 0 && localStart < transition.duration_seconds && activeIdx > 0);
      const base = idx * 2;
      if (transitionActive) {
        const prev = trackItems[activeIdx - 1];
        const progress = localStart / transition.duration_seconds;
        layers.push({ item: prev, zIndex: base + 1, transitionRole: 'outgoing', transition, transitionProgress: progress });
        layers.push({ item: active, zIndex: base + 2, transitionRole: 'incoming', transition, transitionProgress: progress });
      } else {
        layers.push({ item: active, zIndex: base + 1, transitionRole: null, transition: null, transitionProgress: 0 });
      }
    });
    return layers;
  }, [composition, playhead]);

  const audioOnlyItems = useMemo(() => {
    if (!composition) return [];
    return composition.timeline_items.filter((i) => {
      if (i.item_type !== 'audio') return false;
      const track = composition.tracks.find((t) => t.id === i.track_id);
      if (!track || track.hidden || track.muted) return false;
      return playhead >= i.timeline_start_seconds && playhead < i.timeline_start_seconds + i.duration_seconds;
    });
  }, [composition, playhead]);

  const textLayers = useMemo(() => {
    if (!composition) return [];
    return composition.timeline_items.filter((i) => {
      if (i.item_type !== 'text') return false;
      const track = composition.tracks.find((t) => t.id === i.track_id);
      if (!track || track.hidden) return false;
      return playhead >= i.timeline_start_seconds && playhead < i.timeline_start_seconds + i.duration_seconds;
    });
  }, [composition, playhead]);

  const watermark = composition?.branding?.watermark;
  if (videoLayers.length === 0 && audioOnlyItems.length === 0 && textLayers.length === 0 && !composition?.show_safe_zones && !watermark?.enabled) return null;

  return (
    <div className="absolute inset-0">
      {videoLayers.map(({ item, zIndex, transitionRole, transition, transitionProgress }) => (
        <VideoLayer
          key={item.id} item={item} source={siblingSources.find((s) => s.id === item.content_source_id)}
          playhead={playhead} isPlaying={isPlaying} fit={fit} compWidth={composition.width} compHeight={composition.height}
          zIndex={zIndex} onDurationKnown={item.track_id === 'track-v1' ? onDurationKnown : undefined}
          transitionRole={transitionRole} transition={transition} transitionProgress={transitionProgress}
        />
      ))}
      {audioOnlyItems.map((item) => (
        <AudioOnlyLayer key={item.id} item={item} source={siblingSources.find((s) => s.id === item.content_source_id)} playhead={playhead} isPlaying={isPlaying} />
      ))}
      {textLayers.map((item) => (
        <TextLayer key={item.id} item={item} playhead={playhead} compWidth={composition.width} compHeight={composition.height} />
      ))}
      <BrandWatermarkLayer watermark={watermark} />
      {composition?.show_safe_zones && <SafeZoneGuides />}
    </div>
  );
}
